/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {getTime, parseISO} from 'date-fns';
import type {Agent as AgentRemote, AgentInput} from '../../components/TrusstedAgentLegacy/Types/types';
import type {Agent as AgentLocal, AgentDocument, AgentDocumentRemote, NewAgent} from '../../types/agent';

export const mapAgentRemoteToLocal = (remoteAgent: AgentRemote): AgentLocal => {
  return {
    agentId: remoteAgent.agentId,
    createdAt: remoteAgent.createdAt,
    abilities:
      remoteAgent.tools?.map((tool) => ({
        abilityId: tool.toolId,
        name: tool.name,
        instructions: tool.description,
        functionType: tool.functionType,
        userVariables: tool.userVariables || {},
        inputs: tool.parameters.map((parameter) => ({
          name: parameter.name,
          instructions: parameter.description,
          required: parameter.required,
        })),
        postActions: tool.postActions?.map((actionName: string) => ({name: actionName})) || [],
      })) || [],
    faqs: remoteAgent.faq || [],
    isDefault: remoteAgent.published,
    name: remoteAgent.name,
    persona: remoteAgent.description,
    voiceId: remoteAgent.voiceId,
    voiceProvider: remoteAgent.voiceProvider,
    phoneNumbers: remoteAgent.phoneNumbers,
    greeting: remoteAgent.greeting || 'Hey, how can I help you today?',
    updatedAt: remoteAgent.updatedAt,
    documents: remoteAgent.documents || [],
  };
};

export const mapAgentLocalToRemote = (localAgent: AgentLocal | NewAgent): AgentInput => {
  return {
    agentId: 'agentId' in localAgent ? localAgent.agentId : undefined,
    description: localAgent.persona,
    faq: localAgent.faqs,
    name: localAgent.name,
    published: localAgent.isDefault,
    voiceId: localAgent.voiceId,
    voiceProvider: localAgent.voiceProvider,
    phoneNumbers: localAgent.phoneNumbers,
    greeting: localAgent.greeting,
    tools:
      localAgent.abilities?.map((ability) => ({
        toolId: ability.abilityId,
        name: ability.name,
        description: ability.instructions,
        functionType: ability.functionType,
        userVariables: ability.userVariables,
        parameters: ability.inputs.map((input) => ({
          name: input.name,
          description: input.instructions,
          type: 'string',
          required: input.required,
        })),
        postActions: ability.postActions?.map((action) => action.name),
      })) || [],
  };
};

export const mapAgentDocumentRemoteToLocal = (remoteDocuments: AgentDocumentRemote[]): AgentDocument[] => {
  return remoteDocuments.map((remoteDocument) => ({
    filename: remoteDocument.filename,
    size: remoteDocument.size,
    updatedAt: getTime(parseISO(remoteDocument.last_modified)),
  }));
};
